/**
 * SEARCH-002 — Constantes, types purs et helpers du moteur de palette (⌘K).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` (aucun changement de logique) :
 * registres de création/actions, recents (localStorage), navigation 2D spatiale,
 * types partagés. Consommé par `useSearchPalette`, `SearchPaletteBody` et ses
 * sous-composants, ré-exposé au besoin par le hub `SearchPalette`.
 */

import { ArrowRight, Plus, Settings as SettingsIcon } from 'lucide-react'
import { ApiError } from '@/lib/api-error'
import type { SourceType } from '@/lib/searchCategories'

export const DISPLAY_CAP = 5 // lignes visibles / groupe
export const IDENTIFIER_FIELDS = new Set(['siret', 'siren', 'phone', 'email', 'ref'])
export const RECENTS_MAX = 5

/**
 * Le serveur a coupé la recherche au-delà de son délai (SEARCH-001 §7, `search_timeout`) :
 * la palette le dit (« recherche trop longue, précisez votre saisie ») au lieu de l'erreur
 * générique, parce que la réponse utile est de taper plus, pas de réessayer pareil.
 */
export function estUnDelaiDeRecherche(err: unknown): boolean {
    return err instanceof ApiError && err.code.toLowerCase() === 'search_timeout'
}

/** Indice clavier dépendant de la plateforme (⌘ macOS / Ctrl ailleurs). */
export function isMac(): boolean {
    if (typeof navigator === 'undefined') return false
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent)
}
/** Libellé du raccourci « lancer la création » : ⌘↵ (macOS) / Ctrl+↵ (autres). */
export const CREATE_SHORTCUT_LABEL = isMac() ? '⌘↵' : 'Ctrl+↵'

/** Cible du lecteur « Aperçu » plein écran (§6.2). */
export interface QuickLookTarget {
    fileId: string
    filename: string
    mime: string | null
    /** Entité PROPRIÉTAIRE du fichier (≠ entité courante en cross-entités) → en-tête
     *  GED correct pour l'URL signée ; sinon 404 « Aperçu indisponible » sur un doc d'une autre entité. */
    entityId: string | null
}

// ---------------------------------------------------------------------------
// Création contextuelle (complément §5) — uniquement les types réellement
// créables en top-level (un contact/intervenant se crée DANS un client → pas de
// route autonome, donc jamais proposé ici pour éviter un lien mort).
// ---------------------------------------------------------------------------
export interface CreatableType {
    type: SourceType
    permission: string
    route: string
    i18nKey: string
}
// `?create=1` ouvre la modale de création sur la page cible ; `&q=` la pré-remplit
// (complément §5 : « aller jusqu'au bout » — modale ouverte + nom pré-rempli).
// ORDRE = priorité par défaut (sans résultat / égalité) : **client d'abord** (cas le
// plus fréquent quand on tape un mot), puis apprenant, puis lieu. Quand il y a des
// résultats, l'ordre réel reste piloté par le nombre de hits par type (cf. createOptions).
export const CREATABLE: CreatableType[] = [
    { type: 'client', permission: 'clients:create', route: '/clients?create=1', i18nKey: 'client' },
    { type: 'learner', permission: 'learners:create', route: '/learners?create=1', i18nKey: 'learner' },
    { type: 'location', permission: 'locations:create', route: '/logistics/locations?create=1', i18nKey: 'location' },
]

export interface QuickAction {
    key: string
    permission: string
    route: string
    icon: typeof Plus
}
// Colonne « Créer » de l'état vide (raccourcis simples, pas « Créer "q" »).
// Ordre = fréquence réelle de création chez un OF (retour PO 2026-08-02) :
// client, devis, apprenant, intervenant. « Nouveau lieu » a été RETIRÉ — un lieu
// se crée une fois puis se réutilise pendant des années ; il occupait une place
// dans un menu où chaque ligne doit gagner sa présence.
export const CREATE_ACTIONS: QuickAction[] = [
    { key: 'new_client', permission: 'clients:create', route: '/clients?create=1', icon: Plus },
    { key: 'new_quote', permission: 'quotes:create', route: '/sales/quotes?create=1', icon: Plus },
    { key: 'new_learner', permission: 'learners:create', route: '/learners?create=1', icon: Plus },
    {
        key: 'new_instructor',
        permission: 'instructors:create',
        route: '/instructors?create=1',
        icon: Plus,
    },
]
// Colonne « Aller à » de l'état vide.
export const GOTO_ACTIONS: QuickAction[] = [
    { key: 'go_catalog', permission: 'catalog:read', route: '/catalog/formations', icon: ArrowRight },
    { key: 'go_directory', permission: 'directory:read', route: '/directory', icon: ArrowRight },
    { key: 'go_settings', permission: 'settings:read', route: '/settings', icon: SettingsIcon },
]

// ---------------------------------------------------------------------------
// Recents (localStorage) — RECHERCHES récentes, namespacé par user.
// ---------------------------------------------------------------------------
function recentQueriesKey(userId: string) {
    return `argalis-globalsearch-recent-queries:${userId}`
}
export function readRecentQueries(userId: string): string[] {
    try {
        const raw = window.localStorage.getItem(recentQueriesKey(userId))
        const parsed = raw ? (JSON.parse(raw) as unknown) : []
        return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === 'string') : []
    } catch {
        return []
    }
}
export function writeRecentQueries(userId: string, items: string[]) {
    try {
        window.localStorage.setItem(recentQueriesKey(userId), JSON.stringify(items.slice(0, RECENTS_MAX)))
    } catch {
        /* quota / private mode — non bloquant */
    }
}
export function recordQuery(userId: string, raw: string) {
    const query = raw.trim()
    if (query.length < 2) return
    const existing = readRecentQueries(userId).filter((r) => r.toLowerCase() !== query.toLowerCase())
    writeRecentQueries(userId, [query, ...existing])
}

export interface ApiHit {
    result_type: SourceType
    id: string
    label: string
    secondary: string | null
    matched_field: string | null
    entity_id: string
    organization_id: string | null
    origin_type: SourceType | null
    origin_id: string | null
    avatar_url: string | null
}

/**
 * Navigation 2D « quadrillage » de l'état vide (complément §2) : à partir de
 * l'élément courant, trouve le voisin le PLUS PROCHE géométriquement dans la
 * direction pressée (haut/bas/gauche/droite). Robuste à n'importe quelle
 * disposition (colonnes + pastilles en flex-wrap) : on raisonne sur les RECTS
 * réels. Chaque élément navigable porte `data-nav="<value cmdk>"`. Retourne la
 * valeur cible (à passer à `setSelectedValue`) ou `null` s'il n'y a rien dans
 * cette direction (on laisse alors cmdk gérer le bord).
 */
export function nextSpatialValue(
    root: Element,
    dir: 'up' | 'down' | 'left' | 'right',
    currentValue: string,
): string | null {
    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-nav]'))
    if (items.length === 0) return null
    const current = items.find((el) => el.getAttribute('data-nav') === currentValue) ?? items[0]
    const cr = current.getBoundingClientRect()
    const cx = cr.left + cr.width / 2
    const cy = cr.top + cr.height / 2
    const horizontal = dir === 'left' || dir === 'right'
    let best: HTMLElement | null = null
    let bestScore = Infinity
    for (const el of items) {
        if (el === current) continue
        const r = el.getBoundingClientRect()
        const dx = r.left + r.width / 2 - cx
        const dy = r.top + r.height / 2 - cy
        const inDir =
            dir === 'right' ? dx > 4 : dir === 'left' ? dx < -4 : dir === 'down' ? dy > 4 : dy < -4
        if (!inDir) continue
        // Distance sur l'axe principal + forte pénalité sur l'axe transverse →
        // on privilégie le voisin « bien aligné » dans la direction.
        const primary = horizontal ? Math.abs(dx) : Math.abs(dy)
        const cross = horizontal ? Math.abs(dy) : Math.abs(dx)
        const score = primary + cross * 2.5
        if (score < bestScore) {
            bestScore = score
            best = el
        }
    }
    return best?.getAttribute('data-nav') ?? null
}

export const ARROW_DIR: Record<string, 'up' | 'down' | 'left' | 'right'> = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
}

// Action de l'état « aucun résultat », NAVIGABLE au clavier (flèches + Entrée).
// `value` sert à la fois de cible de navigation 2D (`data-nav`) et de clé de
// dispatch pour l'Entrée. Ce sont des boutons (pas des CommandItem) → cmdk ne les
// gère pas : la sélection est portée par `selectedValue` (bague visuelle).
export interface NoResultAction {
    value: string
    kind: 'create' | 'help'
    variant: 'default' | 'ai' | 'primary'
    icon: typeof Plus
    create?: CreatableType
    run: () => void
}
